import {
  GetFrontNotificheSortDirection,
  GetFrontNotificheSortField,
  type GetBackNotificheParams,
  type GetFrontNotificheParams,
} from '@framework/api-client';
import { AuthHeaderFlags } from '@framework/auth';
import { useNotificationsContext } from '@framework/common-web';
import { useTranslation } from '@framework/i18n';
import { message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useInterval } from 'usehooks-ts';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_NOTIFICATION_FLAG_MS_DELAY = 120 * 1000;
const NOTIFICATION_FLAG_QUERY = {
  flVista: false,
  sortField: GetFrontNotificheSortField.DATA_NOTIFICA,
  sortDirection: GetFrontNotificheSortDirection.DESC,
  firstResult: 0,
  maxResults: 1,
};

/**
 * Hook che ritorna le notifiche utente.
 * Le notifiche vengono riaggiornate ogni `delay` millisecondi.
 * Se stop è true, le notifiche non vengono aggiornate
 */
export function useNotification(
  query: GetFrontNotificheParams | GetBackNotificheParams,
) {
  const { useGetNotifications, getGetNotificationsQueryKey } =
    useNotificationsContext();

  const apiResult = useGetNotifications(query, {
    query: {
      keepPreviousData: true,
    },
  });
  const notificationUpdate = useNotificationUpdate();

  // Invalido la key e refetch delle notifiche ad intervalli regolari
  const queryClient = useQueryClient();
  const refetch = useCallback(() => {
    queryClient.invalidateQueries(getGetNotificationsQueryKey(query));
  }, [query, queryClient]);

  const notifications = apiResult.data?.data?.list;

  const pageSize = query.maxResults ?? DEFAULT_PAGE_SIZE;
  const pageNumber =
    Math.floor((query.firstResult ?? 0) / query.maxResults!) + 1;

  const meta = {
    pageSize,
    pageNumber,
    total: apiResult.data?.data?.totalRecs,
  };

  return {
    apiResult,
    notifications,
    notificationUpdate,
    meta,
    refetch,
  };
}

/**
 * Gestisce il meccanismo che indica quando c'è almeno una nuova notifica non letta.
 * Salva le informazioni nel localStorage così che siano sempre disponibili.
 */
function useNotificationUpdate() {
  const { t } = useTranslation();
  //  Seleziono solo l'ultima notifica creata
  const [unreadTotal, setUnreadTotal] = useState(0);
  const { getNotifications, useGetNotifications } = useNotificationsContext();

  const apiResult = useGetNotifications(NOTIFICATION_FLAG_QUERY, {
    request: {
      headers: {
        // Non dobbiamo rinnovare la sessione quando siamo in polling
        [AuthHeaderFlags.IsPolling]: 'true',
      },
    },
  });
  const notification = apiResult.data?.data?.list?.[0];
  //const unread = localStorage.getItem(READ_NOTIFICATION_STORAGE_KEY) === '1';

  // Resetto il flag di unread per non mostrare più il badge
  const resetUnreadFlag = useCallback(async () => {
    try {
      const result = await getNotifications(NOTIFICATION_FLAG_QUERY);
      setUnreadTotal(result.data?.totalRecs ?? 0);
    } catch (error) {
      message.error(
        t(
          'error-while-resetting-notification-flag',
          'Errore durante il reset del flag di notifica',
        ),
      );
      console.error('Errore durante il reset del flag di notifica', { error });
    }
    // TODO/FWP Verificare utilità della dipendenza
  }, [localStorage]);

  // Invalido la key e refetch delle notifiche ad intervalli regolari
  useInterval(() => {
    apiResult.refetch();
  }, DEFAULT_NOTIFICATION_FLAG_MS_DELAY);

  // Ad ogni aggiornamento della notifica, aggiorno coerentemente anche il flag
  useEffect(() => {
    setUnreadTotal(apiResult.data?.data?.totalRecs ?? 0);
  }, [notification]);

  return {
    unreadTotal,
    resetUnreadFlag,
  };
}
